import { useEffect, useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { logSecurityEvent } from '@/utils/securityMonitoring';
import { useGeo, BLOCKED_COUNTRIES } from '@/context/GeoContext';
import { supabase } from '@/integrations/supabase/client';

// SEO crawler detection - only allow trusted Western crawlers
const SEO_CRAWLERS = ['googlebot', 'bingbot', 'duckduckbot'];

// Geo detection services with fallback - ALL HTTPS to prevent mixed content blocking
const GEO_SERVICES = [
  {
    name: 'ipwho',
    url: 'https://ipwho.is/',
    parse: (data: any) => ({
      country_code: data.country_code,
      country_name: data.country,
      error: !data.success
    }),
    timeout: 2500,
  },
  {
    name: 'ipapi.co',
    url: 'https://ipapi.co/json/',
    parse: (data: any) => ({ 
      country_code: data.country_code, 
      country_name: data.country_name,
      error: !!data.error
    }),
    timeout: 3000,
  },
  {
    name: 'geojs',
    url: 'https://get.geojs.io/v1/ip/country.json',
    parse: (data: any) => ({
      country_code: data.country,
      country_name: data.name || data.country,
      error: !data.country
    }),
    timeout: 2500,
  },
];

interface GeoData {
  country_code?: string;
  country_name?: string;
  error?: string | boolean;
}

export const GeoBlocker = ({ children }: { children: React.ReactNode }) => {
  const [isBlocked, setIsBlocked] = useState(false);
  // OPTIMIZATION: Show content immediately while geo-check runs in background
  // Only block AFTER we confirm user is from blocked country (reduces LCP by 200-400ms)
  const [isLoading, setIsLoading] = useState(false);
  const [geoCheckComplete, setGeoCheckComplete] = useState(false);
  const [countryName, setCountryName] = useState('');
  const { setGeoState } = useGeo();

  // Fetch geo data with server-first approach and fallback services
  const fetchGeoData = useCallback(async (): Promise<GeoData | null> => {
    // 1) Prefer server-verified geo verdict (based on cf-ipcountry)
    try {
      const { data, error } = await supabase.functions.invoke('geo-check');
      if (!error && data?.countryCode) {
        return {
          country_code: data.countryCode,
          country_name: data.countryName || data.countryCode,
        } as GeoData;
      }
    } catch {
      // ignore, fall back
    }

    // 2) Fallback to third-party geo providers
    for (const service of GEO_SERVICES) {
      try {
        console.log(`[GeoBlocker] Trying ${service.name}...`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), service.timeout);

        const response = await fetch(service.url, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) continue;

        const rawData = await response.json();
        const data = service.parse(rawData);

        if (!data.error && data.country_code) {
          console.log(`[GeoBlocker] Got geo data from ${service.name}:`, data.country_code);
          return data;
        }
      } catch (e) {
        console.log(`[GeoBlocker] ${service.name} failed:`, e instanceof Error ? e.message : 'unknown');
        continue;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    const checkGeoLocation = async () => {
      try {
        // Check if user agent is a crawler
        const userAgent = navigator.userAgent.toLowerCase();
        const isCrawler = SEO_CRAWLERS.some(bot => userAgent.includes(bot));
        
        if (isCrawler) {
          console.log('[GeoBlocker] SEO crawler detected, allowing access');
          logSecurityEvent({
            type: 'geo_blocker_crawler_allowed',
            action: 'allowed',
            details: { user_agent: userAgent }
          });
          setGeoState({
            isChecked: true,
            isBlocked: false,
            countryCode: 'US', // Treat crawlers as US for analytics
            countryName: 'United States',
          });
          setGeoCheckComplete(true);
          return;
        }

        // Check localStorage cache FIRST before any network calls
        // This is critical for performance - avoid blocking the critical path
        const cached = localStorage.getItem('geo_check');
        if (cached) {
          try {
            const { timestamp, blocked, country, country_code } = JSON.parse(cached);
            // 7 days for allowed countries, 1 day for blocked countries
            const cacheExpiry = blocked ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
            
            if (Date.now() - timestamp < cacheExpiry) {
              console.log(`[GeoBlocker] Cache hit: ${country} (${blocked ? 'blocked' : 'allowed'})`);
              logSecurityEvent({
                type: 'geo_blocker_cache_hit',
                country,
                country_code,
                action: blocked ? 'blocked' : 'allowed',
              });
              setGeoState({
                isChecked: true,
                isBlocked: blocked,
                countryCode: country_code || null,
                countryName: country,
              });
              // For cached blocked users, show block immediately
              if (blocked) {
                setIsBlocked(true);
                setCountryName(country);
                setIsLoading(true); // Show loading briefly before block screen
              }
              setGeoCheckComplete(true);
              return;
            }
          } catch (e) {
            // Invalid cache, continue with fresh check
            localStorage.removeItem('geo_check');
          }
        }

        // Fetch geo location with fallback services IN BACKGROUND (non-blocking for LCP)
        console.log('[GeoBlocker] Performing fresh geo-location check in background...');
        const data = await fetchGeoData();

        // FAIL OPEN: Allow access if all geo services fail (better UX than blocking everyone)
        // This is a conscious trade-off: we prefer false negatives (allowing blocked users) 
        // over false positives (blocking legitimate users)
        if (!data || !data.country_code) {
          console.warn('[GeoBlocker] All geo services failed, allowing access (fail open)');
          logSecurityEvent({
            type: 'geo_blocker_all_services_failed',
            action: 'allowed',
            details: { reason: 'all_geo_services_failed_fail_open' }
          });
          setGeoState({
            isChecked: true,
            isBlocked: false,
            countryCode: null,
            countryName: 'Unknown',
          });
          setGeoCheckComplete(true);
          return;
        }

        const countryCode = data.country_code;
        const blocked = BLOCKED_COUNTRIES.includes(countryCode);
        const country = data.country_name || countryCode;

        console.log(`[GeoBlocker] Result: ${country} (${countryCode}) - ${blocked ? 'BLOCKED' : 'ALLOWED'}`);

        // Cache the result
        localStorage.setItem('geo_check', JSON.stringify({
          timestamp: Date.now(),
          blocked,
          country,
          country_code: countryCode,
        }));

        logSecurityEvent({
          type: blocked ? 'geo_blocker_blocked' : 'geo_blocker_allowed',
          country,
          country_code: countryCode,
          action: blocked ? 'blocked' : 'allowed',
        });

        // Update GeoContext for analytics
        setGeoState({
          isChecked: true,
          isBlocked: blocked,
          countryCode,
          countryName: country,
        });

        // Only update UI if user is blocked (otherwise content already showing)
        if (blocked) {
          setIsBlocked(true);
          setCountryName(country);
          setIsLoading(true); // Show loading briefly before block screen
        }
        setGeoCheckComplete(true);
        
      } catch (error) {
        // FAIL OPEN: Allow on any error (better UX than blocking everyone)
        console.warn('[GeoBlocker] Check error, allowing access (fail open):', error);
        logSecurityEvent({
          type: 'geo_blocker_exception',
          action: 'allowed',
          details: { error: error instanceof Error ? error.message : 'unknown_error', reason: 'exception_fail_open' }
        });
        setGeoState({
          isChecked: true,
          isBlocked: false,
          countryCode: null,
          countryName: 'Unknown',
        });
        setGeoCheckComplete(true);
      }
    };

    // Delay geo check more on mobile (slower connections) to prioritize LCP
    // Use matchMedia instead of window.innerWidth to avoid forced reflow
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    const delay = isMobile ? 200 : 100; // 200ms on mobile, 100ms on desktop
    const timer = setTimeout(checkGeoLocation, delay);
    return () => clearTimeout(timer);
  }, [fetchGeoData, setGeoState]);

  // Show loading spinner only when transitioning to blocked state
  // (user was allowed, then confirmed blocked - rare edge case)
  if (isLoading && isBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show blocked message if user is from blocked country
  if (isBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-2xl w-full border-destructive/50">
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl">Service Not Available</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              We're sorry, but YachtValues services are not currently available in {countryName || 'your region'}.
            </p>
            <p className="text-sm text-muted-foreground">
              This restriction is in place to ensure the best service quality for our supported markets.
            </p>
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                If you believe this is an error or you're using a VPN, please try disabling it and refreshing the page.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Allow access
  return <>{children}</>;
};
